import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { adminRevenuePeriods, type RevenuePeriod, type RevenuePeriodInput } from '../../api/admin';
import { Plus, Pencil, Trash2, Download, ChevronDown } from 'lucide-react';

const fmt = (n: number) => `₹${(Number.isFinite(n) ? n : 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const intFmt = (n: number) => (Number.isFinite(n) ? n : 0).toLocaleString('en-IN');
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayISO = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
const dateShort = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';

// Group a raw numeric string (digits, optional decimal) with Indian commas.
const groupIN = (raw: string) => {
  if (!raw) return '';
  const hasDot = raw.includes('.');
  const [intPart, ...rest] = raw.split('.');
  const clean = intPart.replace(/[^0-9]/g, '');
  const grouped = clean ? Number(clean).toLocaleString('en-IN') : '';
  return hasDot ? `${grouped || '0'}.${rest.join('').replace(/[^0-9]/g, '')}` : grouped;
};

function downloadCSV(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

type Kind = 'int' | 'money';
interface FieldDef { key: string; label: string; kind: Kind }
const GROUPS: { title: string; fields: FieldDef[] }[] = [
  { title: 'Counts', fields: [
    { key: 'shipway_orders', label: 'Shipway orders', kind: 'int' },
    { key: 'porter_orders', label: 'Porter orders', kind: 'int' },
    { key: 'never_shipped_orders', label: 'Never shipped orders', kind: 'int' },
    { key: 'total_orders', label: 'Total orders', kind: 'int' },
    { key: 'rto_orders', label: 'RTO orders', kind: 'int' },
    { key: 'cod_count', label: '# COD orders', kind: 'int' },
    { key: 'prepaid_count', label: '# Prepaid orders', kind: 'int' },
    { key: 'porter_parcel_count', label: '# Porter parcels', kind: 'int' },
    { key: 'qty_sold', label: 'Qty sold', kind: 'int' },
  ] },
  { title: 'Revenue (₹)', fields: [
    { key: 'cod_revenue', label: 'COD revenue', kind: 'money' },
    { key: 'prepaid_revenue', label: 'Prepaid revenue', kind: 'money' },
    { key: 'porter_revenue', label: 'Porter revenue', kind: 'money' },
    { key: 'total_revenue', label: 'Total revenue', kind: 'money' },
  ] },
  { title: 'Costs (₹)', fields: [
    { key: 'packaging_charges', label: 'Packaging charges', kind: 'money' },
    { key: 'shipway_shipping_rto', label: 'Shipway shipping + RTO', kind: 'money' },
    { key: 'advertising_spent', label: 'Advertising spent', kind: 'money' },
    { key: 'other_charges', label: 'Other charges', kind: 'money' },
    { key: 'shipway_remittance_charges', label: 'Shipway remittance charges', kind: 'money' },
    { key: 'total_charges', label: 'Total charges', kind: 'money' },
  ] },
  { title: 'Results (₹)', fields: [
    { key: 'revenue_after_expenses', label: 'Revenue after Expenses', kind: 'money' },
    { key: 'shipway_bank_dispersal', label: 'Shipway bank dispersal', kind: 'money' },
  ] },
  { title: 'In-house', fields: [
    { key: 'inhouse_qty', label: 'In-house qty (use)', kind: 'int' },
    { key: 'inhouse_cost', label: 'In-house cost (₹)', kind: 'money' },
  ] },
];
const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);
// Auto-computed fields — read-only in the form.
const COMPUTED: Record<string, string[]> = {
  total_orders: ['shipway_orders', 'porter_orders', 'never_shipped_orders'],
  total_revenue: ['cod_revenue', 'prepaid_revenue', 'porter_revenue'],
  total_charges: ['packaging_charges', 'shipway_shipping_rto', 'shipway_remittance_charges', 'advertising_spent', 'other_charges'],
};
// Difference-type computed fields: value = parts[0] - parts[1].
const COMPUTED_DIFF: Record<string, [string, string]> = {
  revenue_after_expenses: ['total_revenue', 'total_charges'],
};
const IS_AUTO = (key: string) => key in COMPUTED || key in COMPUTED_DIFF;
const COMPUTED_HINT: Record<string, string> = {
  total_orders: '= shipway + porter + never shipped',
  total_revenue: '= COD + prepaid + porter',
  total_charges: '= packaging + shipping/RTO + remittance + ads + other',
  revenue_after_expenses: '= total revenue − total charges',
};
const FIELD_BY_KEY: Record<string, FieldDef> = Object.fromEntries(ALL_FIELDS.map((f) => [f.key, f]));

interface RModal { id: string | null; v: Record<string, string> }
const emptyValues = (): Record<string, string> => {
  const v: Record<string, string> = { period_from: todayISO(), period_to: todayISO(), comments: '' };
  for (const f of ALL_FIELDS) v[f.key] = '';
  return v;
};

export default function RevenueTab({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<RevenuePeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modal, setModal] = useState<RModal | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; count: number } | null>(null);
  const [datePreset, setDatePreset] = useState<'all' | 'period'>('all');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const inp = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
           : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const label = `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const header = `text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`;

  const load = () => {
    setLoading(true);
    adminRevenuePeriods.list().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const visibleRows = useMemo(() => {
    const sortDesc = (arr: RevenuePeriod[]) => arr.slice().sort((a, b) => (b.period_from ?? '').localeCompare(a.period_from ?? ''));
    if (datePreset === 'period') return sortDesc(rows.filter(r => r.id === selectedPeriodId));
    return sortDesc(rows);
  }, [rows, datePreset, selectedPeriodId]);
  const totRevenue = useMemo(() => visibleRows.reduce((s, r) => s + (r.total_revenue || 0), 0), [visibleRows]);
  const totNet = useMemo(() => visibleRows.reduce((s, r) => s + (r.revenue_after_expenses || 0), 0), [visibleRows]);

  const toggleExpand = (id: string) => setExpandedId((cur) => (cur === id ? null : id));
  const setV = (key: string, val: string) => setModal((m) => (m ? { ...m, v: { ...m.v, [key]: val } } : m));

  const failWith = (msg: string) => {
    setError(msg);
    requestAnimationFrame(() => modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const save = async () => {
    if (!modal) return;
    if (!modal.v.period_from || !modal.v.period_to) { failWith('Enter the period From and To dates'); return; }
    if (modal.v.period_from > modal.v.period_to) { failWith('Period "From" must be on or before "To"'); return; }
    const newFrom = modal.v.period_from;
    const newTo = modal.v.period_to;
    const conflict = rows.find((r) => {
      if (modal.id && r.id === modal.id) return false;
      const rFrom = r.period_from?.slice(0, 10) || '0000-01-01';
      const rTo = r.period_to?.slice(0, 10) || '9999-12-31';
      return newFrom <= rTo && newTo >= rFrom;
    });
    if (conflict) {
      failWith(`Period overlaps with an existing entry: ${dateShort(conflict.period_from)} → ${dateShort(conflict.period_to)}`);
      return;
    }
    setSaving(true);
    setError('');
    const payload: Record<string, any> = {
      period_from: modal.v.period_from,
      period_to: modal.v.period_to,
      comments: modal.v.comments || null,
    };
    for (const f of ALL_FIELDS) {
      const raw = modal.v[f.key];
      payload[f.key] = f.kind === 'int' ? (parseInt(raw, 10) || 0) : (parseFloat(raw) || 0);
    }
    // Overwrite auto-computed totals.
    const sumParts = (parts: string[], kind: Kind) =>
      parts.reduce((s, p) => s + (kind === 'int' ? (parseInt(payload[p] ?? modal.v[p], 10) || 0) : (parseFloat(String(payload[p] ?? modal.v[p])) || 0)), 0);
    for (const [key, parts] of Object.entries(COMPUTED)) {
      const kind = ALL_FIELDS.find((f) => f.key === key)!.kind;
      payload[key] = sumParts(parts, kind);
    }
    for (const [key, [a, b]] of Object.entries(COMPUTED_DIFF)) {
      payload[key] = (payload[a] ?? 0) - (payload[b] ?? 0);
    }
    try {
      if (modal.id) await adminRevenuePeriods.update(modal.id, payload as Partial<RevenuePeriodInput>);
      else await adminRevenuePeriods.create(payload as RevenuePeriodInput);
      setModal(null);
      load();
    } catch (e) {
      failWith(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await adminRevenuePeriods.delete(id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const openAdd = () => { setModal({ id: null, v: emptyValues() }); setError(''); };
  const openEdit = (r: RevenuePeriod) => {
    const v: Record<string, string> = {
      period_from: r.period_from ? r.period_from.slice(0, 10) : todayISO(),
      period_to: r.period_to ? r.period_to.slice(0, 10) : todayISO(),
      comments: r.comments || '',
    };
    for (const f of ALL_FIELDS) v[f.key] = String((r as any)[f.key] ?? 0);
    setModal({ id: r.id, v });
    setError('');
  };

  const exportCsv = () => {
    const head = ['From', 'To', ...ALL_FIELDS.map((f) => f.label), 'Comments'];
    const csvRows = visibleRows.map((r) => [
      r.period_from ?? '', r.period_to ?? '',
      ...ALL_FIELDS.map((f) => (r as any)[f.key] ?? 0),
      r.comments ?? '',
    ]);
    downloadCSV('ZeroSmoke_Revenue.csv', head, csvRows);
  };

  const dispVal = (r: RevenuePeriod, f: FieldDef) => (f.kind === 'money' ? fmt((r as any)[f.key]) : intFmt((r as any)[f.key]));

  // Live formula computation for the modal.
  const computeAuto = (key: string, kind: Kind): number => {
    if (!modal) return 0;
    if (key in COMPUTED_DIFF) {
      const [a, b] = COMPUTED_DIFF[key];
      const kindA = ALL_FIELDS.find((f) => f.key === a)?.kind ?? kind;
      const kindB = ALL_FIELDS.find((f) => f.key === b)?.kind ?? kind;
      return computeAuto(a, kindA) - computeAuto(b, kindB);
    }
    return COMPUTED[key].reduce((s, p) => s + (kind === 'int' ? (parseInt(modal.v[p], 10) || 0) : (parseFloat(modal.v[p]) || 0)), 0);
  };

  const renderField = (f: FieldDef) => {
    const auto = IS_AUTO(f.key);
    return (
      <div key={f.key}>
        <label className={label}>{f.label}{auto && <span className={muted}> (auto)</span>}</label>
        <input
          type="text"
          inputMode={f.kind === 'money' ? 'decimal' : 'numeric'}
          value={auto ? groupIN(String(computeAuto(f.key, f.kind))) : groupIN(modal!.v[f.key])}
          onChange={auto ? undefined : (e) => setV(f.key, e.target.value.replace(f.kind === 'money' ? /[^0-9.]/g : /[^0-9]/g, ''))}
          readOnly={auto}
          className={`${inp} ${auto ? 'opacity-70 cursor-not-allowed' : ''}`}
          placeholder="0"
        />
        {auto && <p className={`text-[11px] mt-0.5 ${muted}`}>{COMPUTED_HINT[f.key]}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg">
          <Plus size={16} /> Add Revenue Period
        </button>
        <button onClick={exportCsv}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
          <Download size={15} /> Export CSV
        </button>
        <div className={`ml-auto flex items-center gap-4 text-sm ${muted}`}>
          <span>Revenue: <b>{fmt(totRevenue)}</b></span>
          <span>Rev. after Exp.: <b className={totNet >= 0 ? 'text-emerald-500' : 'text-red-500'}>{fmt(totNet)}</b></span>
        </div>
      </div>

      {/* Period date filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Period:</span>
        <button
          onClick={() => { setDatePreset('all'); setSelectedPeriodId(''); }}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
            datePreset === 'all'
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
              : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}
        >
          All time
        </button>
        <select
          value={selectedPeriodId}
          onChange={e => {
            const id = e.target.value;
            setSelectedPeriodId(id);
            setDatePreset(id ? 'period' : 'all');
          }}
          className={`px-2 py-1 rounded-lg border text-xs font-semibold transition-all ${
            datePreset === 'period'
              ? 'bg-emerald-500 text-white border-emerald-500'
              : isDark ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
          }`}
        >
          <option value="">Select period…</option>
          {[...rows]
            .sort((a, b) => (b.period_from ?? '').localeCompare(a.period_from ?? ''))
            .map(p => (
              <option key={p.id} value={p.id}>
                {dateShort(p.period_from)} → {dateShort(p.period_to)}
              </option>
            ))}
        </select>
      </div>

      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
              <th className="font-medium py-3 px-4">Period</th>
              <th className="font-medium py-3 px-4 text-right">Total orders</th>
              <th className="font-medium py-3 px-4 text-right">Qty sold</th>
              <th className="font-medium py-3 px-4 text-right">Total revenue</th>
              <th className="font-medium py-3 px-4 text-right">Revenue after Expenses</th>
              <th className="font-medium py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">No revenue periods yet</td></tr>}
            {!loading && rows.length > 0 && visibleRows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">No periods match the current filter</td></tr>}
            {visibleRows.map((r) => {
              const open = expandedId === r.id;
              return (
                <Fragment key={r.id}>
                  <tr onClick={() => toggleExpand(r.id)} className={`border-b cursor-pointer select-none ${isDark ? 'border-gray-800 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className="py-3 px-4 whitespace-nowrap">{dateShort(r.period_from)} <span className={muted}>→</span> {dateShort(r.period_to)}</td>
                    <td className="py-3 px-4 text-right">{intFmt(r.total_orders)}</td>
                    <td className="py-3 px-4 text-right">{intFmt(r.qty_sold)}</td>
                    <td className="py-3 px-4 text-right">{fmt(r.total_revenue)}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${(r.revenue_after_expenses ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(r.revenue_after_expenses)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} title="Edit" aria-label="Edit" className="text-gray-400 hover:text-emerald-500"><Pencil size={15} /></button>
                        <button onClick={(e) => { e.stopPropagation(); toggleExpand(r.id); }} title="Details" aria-label="Details" className="text-gray-400 hover:text-emerald-500"><ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} /></button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          if (r.inventory_sales_count && r.inventory_sales_count > 0) {
                            setDeleteConfirm({ id: r.id, count: r.inventory_sales_count });
                          } else if (confirm('Delete this revenue period?')) {
                            remove(r.id);
                          }
                        }} title="Delete" aria-label="Delete" className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className={isDark ? 'bg-gray-800/30' : 'bg-gray-50'}>
                      <td colSpan={6} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {GROUPS.map((g) => (
                            <div key={g.title}>
                              <p className={header}>{g.title}</p>
                              <div className="space-y-1.5 text-sm">
                                {g.fields.map((f) => {
                                  const isTotalRow = f.key === 'total_orders' || f.key === 'total_revenue' || f.key === 'total_charges' || f.key === 'revenue_after_expenses';
                                  const hasSep = isTotalRow || f.key === 'qty_sold';
                                  const hasLineAfter = f.key === 'total_orders' || f.key === 'total_charges';
                                  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
                                  const extraMt = f.key === 'shipway_bank_dispersal' ? 'mt-8' : f.key === 'qty_sold' ? 'mt-8' : '';
                                  const extraMb = f.key === 'shipway_bank_dispersal' ? 'mb-4' : '';
                                  const sepMt = extraMt || 'mt-0.5';
                                  const borderCls = `${hasSep ? `border-t pt-1.5 ${sepMt} ${borderColor}` : ''} ${hasLineAfter ? `border-b pb-1.5 ${borderColor}` : ''} ${!hasSep ? extraMt : ''} ${extraMb}`;
                                  return (
                                    <div key={f.key} className={`flex justify-between gap-4 ${borderCls}`}>
                                      <span className={`${muted} ${isTotalRow ? 'font-semibold' : ''}`}>{f.label}</span>
                                      <span className={f.key === 'revenue_after_expenses' ? `font-semibold ${(r.revenue_after_expenses ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}` : isTotalRow ? 'font-semibold' : ''}>{dispVal(r, f)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className={`mt-4 pt-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                          {r.comments && <div className={`mb-1 text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}><span className={`font-medium ${muted}`}>Comment: </span>{r.comments}</div>}
                          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Created {dateShort(r.created_at)} · Updated {dateShort(r.updated_at)}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-sm rounded-2xl border p-6 ${card}`}>
            <h3 className="font-bold text-lg mb-2">Delete Revenue Period?</h3>
            <p className="text-sm mb-2">This action cannot be undone.</p>
            <p className="text-sm mb-5">
              <span className="font-bold text-red-500">
                ⚠ {deleteConfirm.count} inventory sales {deleteConfirm.count === 1 ? 'entry' : 'entries'} for this period will also be permanently deleted.
              </span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={async () => { await remove(deleteConfirm.id); setDeleteConfirm(null); }}
                className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg text-sm"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <div ref={modalRef} className={`w-full max-w-2xl rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${card}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{modal.id ? 'Edit' : 'Add'} Revenue Period</h3>
            {error && <div className="mb-3 text-sm text-red-500">{error}</div>}

            <div className="space-y-4">
              {/* Period */}
              <div>
                <p className={header}>Period</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>From <span className="text-red-500">*</span></label>
                    <input type="date" value={modal.v.period_from} onChange={(e) => setV('period_from', e.target.value)} className={inp} />
                  </div>
                  <div>
                    <label className={label}>To <span className="text-red-500">*</span></label>
                    <input type="date" value={modal.v.period_to} onChange={(e) => setV('period_to', e.target.value)} className={inp} />
                  </div>
                </div>
              </div>

              {GROUPS.map((g) => (
                <div key={g.title}>
                  <p className={header}>{g.title}</p>
                  {g.title === 'Counts' ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['shipway_orders', 'porter_orders', 'never_shipped_orders', 'total_orders'].map((k) => renderField(FIELD_BY_KEY[k]))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {['rto_orders', 'cod_count', 'prepaid_count'].map((k) => renderField(FIELD_BY_KEY[k]))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {['porter_parcel_count', 'qty_sold'].map((k) => renderField(FIELD_BY_KEY[k]))}
                      </div>
                    </div>
                  ) : g.title === 'Costs (₹)' ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {['packaging_charges', 'shipway_shipping_rto', 'advertising_spent', 'other_charges', 'shipway_remittance_charges'].map((k) => renderField(FIELD_BY_KEY[k]))}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {['total_charges'].map((k) => renderField(FIELD_BY_KEY[k]))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {g.fields.map(renderField)}
                    </div>
                  )}
                </div>
              ))}

              <div>
                <label className={label}>Comments (optional)</label>
                <textarea value={modal.v.comments} onChange={(e) => setV('comments', e.target.value)} className={inp} rows={2} />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setModal(null)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
